/**
 * Ads Campaigns store — Phase 2 overhaul.
 * Tenant-aware ad campaigns backed by `ad_campaigns` + `ad_creatives` + `ad_events`.
 * Legacy `adSlotsStore` continues to serve simple ad-slot images; this store
 * powers the newer campaign-driven placements resolved by `adResolver`.
 */
import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";

export type AdScope = "main_only" | "all_tenants" | "specific_tenants";
export type AdStatus = "draft" | "scheduled" | "active" | "paused" | "ended";

export interface AdCampaign {
  id: string;
  name: string;
  scope: AdScope;
  tenant_ids: string[];
  pages: string[];
  positions: string[];
  status: AdStatus;
  priority: number;
  starts_at: string | null;
  ends_at: string | null;
  default_image_url: string | null;
  default_link_url: string | null;
  ab_variant_group: string | null;
  ab_weight: number;
  created_at: string;
  updated_at: string;
}

export interface AdCreative {
  id: string;
  campaign_id: string;
  tenant_id: string | null;
  image_url: string;
  link_url: string | null;
  label: string | null;
}

interface AdsState {
  campaigns: AdCampaign[];
  creatives: AdCreative[];
  loading: boolean;
  loaded: boolean;
  load: () => Promise<void>;
  create: (patch: Partial<AdCampaign> & { name: string }) => Promise<AdCampaign | null>;
  update: (id: string, patch: Partial<AdCampaign>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  upsertCreative: (row: Omit<AdCreative, "id"> & { id?: string }) => Promise<void>;
  removeCreative: (id: string) => Promise<void>;
}

export const useAdsStore = create<AdsState>()((set, get) => ({
  campaigns: [],
  creatives: [],
  loading: false,
  loaded: false,
  async load() {
    if (get().loading) return;
    set({ loading: true });
    const [c, cr] = await Promise.all([
      supabase.from("ad_campaigns").select("*").order("priority", { ascending: false }),
      supabase.from("ad_creatives").select("*"),
    ]);
    set({
      campaigns: (c.data as AdCampaign[]) || [],
      creatives: (cr.data as AdCreative[]) || [],
      loading: false,
      loaded: true,
    });
  },
  async create(patch) {
    const { data, error } = await supabase
      .from("ad_campaigns")
      .insert({
        name: patch.name,
        scope: patch.scope ?? "main_only",
        tenant_ids: patch.tenant_ids ?? [],
        pages: patch.pages ?? [],
        positions: patch.positions ?? [],
        status: patch.status ?? "draft",
        priority: patch.priority ?? 0,
        starts_at: patch.starts_at ?? null,
        ends_at: patch.ends_at ?? null,
        default_image_url: patch.default_image_url ?? null,
        default_link_url: patch.default_link_url ?? null,
        ab_variant_group: patch.ab_variant_group ?? null,
        ab_weight: patch.ab_weight ?? 100,
      } as any)
      .select("*")
      .single();
    if (error) { console.error(error); return null; }
    set({ campaigns: [data as AdCampaign, ...get().campaigns] });
    return data as AdCampaign;
  },
  async update(id, patch) {
    const { error } = await supabase.from("ad_campaigns").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) { console.error(error); return; }
    set({ campaigns: get().campaigns.map(c => c.id === id ? { ...c, ...patch } as AdCampaign : c) });
  },
  async remove(id) {
    const { error } = await supabase.from("ad_campaigns").delete().eq("id", id);
    if (error) { console.error(error); return; }
    set({ campaigns: get().campaigns.filter(c => c.id !== id) });
  },
  async upsertCreative(row) {
    const { data, error } = await supabase
      .from("ad_creatives")
      .upsert({
        id: row.id,
        campaign_id: row.campaign_id,
        tenant_id: row.tenant_id,
        image_url: row.image_url,
        link_url: row.link_url,
        label: row.label,
      } as any, { onConflict: "campaign_id,tenant_id" })
      .select("*")
      .single();
    if (error) { console.error(error); return; }
    const existing = get().creatives.filter(c => !(c.campaign_id === data.campaign_id && c.tenant_id === data.tenant_id));
    set({ creatives: [...existing, data as AdCreative] });
  },
  async removeCreative(id) {
    const { error } = await supabase.from("ad_creatives").delete().eq("id", id);
    if (error) { console.error(error); return; }
    set({ creatives: get().creatives.filter(c => c.id !== id) });
  },
}));

/** Log impression/click. Best-effort; never throws. */
export async function logAdEvent(input: {
  campaign_id: string;
  tenant_id?: string | null;
  event_type: "impression" | "click";
  page?: string; position?: string; ab_variant_group?: string | null;
}) {
  try {
    await supabase.from("ad_events").insert({
      campaign_id: input.campaign_id,
      tenant_id: input.tenant_id ?? null,
      event_type: input.event_type,
      page: input.page ?? null,
      position: input.position ?? null,
      ab_variant_group: input.ab_variant_group ?? null,
    } as any);
  } catch (e) { /* ignore */ }
}
